// Bilingual content (English + Bahasa Indonesia) for the whole site.
// Structural data (images, links) lives in data/site.js.

export const TRANSLATIONS = {
  en: {
    code: "EN",
    nav: {
      home: "Home",
      about: "About",
      products: "Products",
      quality: "Quality",
      process: "Process",
      gallery: "Gallery",
      contact: "Contact",
    },
    common: {
      getQuote: "Get a Quote",
      enquire: "Enquire",
      downloadSpec: "Download Spec Sheet",
      copy: "Copy number",
      copied: "Copied!",
    },
    hero: {
      overline: "From the Heart of Indonesia",
      title: ["Premium", "Indonesian", "Vanilla Beans"],
      titleAria: "Premium Indonesian Vanilla Beans",
      subtitle:
        "Delivering the finest Indonesian vanilla to food manufacturers, flavour houses, and gourmet brands worldwide.",
      watchStory: "Watch Our Story",
      scroll: "Scroll",
    },
    about: {
      overline: "Our Story",
      title: "Rooted in the volcanic soils of Java",
      p1: "Tropical Vanilla Co. was born from a simple belief — that Indonesia grows some of the most expressive vanilla on earth, and that it deserves to reach the world with its character intact.",
      p2: "We work hand in hand with smallholder farmers across the highlands of Yogyakarta, curing every pod the traditional way and grading it by hand. The result is a deep, dark, creamy vanilla trusted by bakeries, flavour houses and gourmet brands across continents.",
      p3: "From a single vine to a sealed export carton, we own every step — so the quality you taste is the quality we promise.",
      stat1: "100%",
      stat1Label: "Traceable Origin",
      stat2: "Grade A",
      stat2Label: "Gourmet Standard",
    },
    features: {
      overline: "Why Choose",
      title: "Tropical Vanilla",
      items: [
        { title: "Premium Quality", text: "Carefully selected beans with high vanillin content and optimal moisture." },
        { title: "Sustainable Sourcing", text: "Working directly with local farmers using sustainable and ethical practices." },
        { title: "Reliable Supply", text: "Consistent quality and supply all year round to support your business." },
        { title: "Export Expertise", text: "Complete export documentation and professional service you can trust." },
      ],
    },
    products: {
      overline: "Our Products",
      title: "Vanilla, in every form",
      items: [
        {
          name: "Vanilla Beans — Grade A",
          tag: "Gourmet",
          text: "Long, glossy, oil-rich gourmet pods hand-graded for exceptional aroma and flavour.",
          specs: [["Moisture", "30 – 35%"], ["Vanillin", "> 2%"], ["Length", "16 – 20 cm"]],
        },
        {
          name: "Vanilla Extract",
          tag: "Liquid",
          text: "Natural, slow-extracted vanilla made from premium Indonesian beans for pure, rounded taste.",
          specs: [["Type", "Single-fold"], ["Base", "Natural"], ["Use", "Bakery & dairy"]],
        },
        {
          name: "Vanilla Powder",
          tag: "Ground",
          text: "Finely ground whole beans — perfect for bakeries, ice cream, chocolate and beverages.",
          specs: [["Grind", "Fine"], ["Purity", "100% bean"], ["Shelf life", "24 months"]],
        },
        {
          name: "Vanilla Seed (Caviar)",
          tag: "Caviar",
          text: "Tiny black vanilla caviar seeds harvested from split pods — the pure aromatic heart of the vanilla bean.",
          specs: [["Form", "Loose seeds"], ["Purity", "100% seed"], ["Use", "Pastry, ice cream, confectionery"]],
        },
        {
          name: "Vanilla Paste",
          tag: "Paste",
          text: "Rich and aromatic paste made from premium Indonesian beans for convenient use in baking and confectionery.",
          specs: [
            ["Form", "Bold Paste"],
            ["Base", "Powder mix with glycerin"],
            ["Use", "Dessert, bakery, ice cream"],
          ]
        }
      ],
    },
    process: {
      overline: "Our Process",
      title: "From vine to export carton",
      subtitle: "Five deliberate steps, each done by hand, protect the flavour we are known for.",
      steps: [
        { title: "Harvesting", text: "Every pod is hand-picked at perfect maturity, ensuring the deepest concentration of flavour before curing begins." },
        { title: "Curing & Fermentation", text: "Beans are sun-cured and sweated in a traditional cycle over weeks, developing their rich, dark aroma naturally." },
        { title: "Sorting & Grading", text: "Each bean is measured, inspected and graded by hand so every bundle we ship meets a single, consistent standard." },
        { title: "Packaging", text: "Vacuum-sealed in food-grade packaging within a hygienic facility to lock in moisture, aroma and freshness." },
        { title: "Export", text: "On-time worldwide delivery with complete export documentation, handled end-to-end by our team." },
      ],
    },
    quality: {
      overline: "Quality Assurance",
      title: "Held to an international standard",
      text: "Every batch is tested for moisture, vanillin content and food safety before it leaves our facility — so you receive vanilla that performs, consistently.",
      specs: [
        "Moisture content: 30 – 35%",
        "Vanillin content: > 2% (Grade A)",
        "Microbial & pesticide tested",
        "Compliant with international food-safety standards",
        "Full traceability from farm to port",
      ],
      cta: "Request Specifications",
    },
    gallery: { overline: "Gallery", title: "From our plantations" },
    marquee: ["Premium Export", "Sustainable Agriculture", "Gourmet Vanilla", "Indonesian Heritage"],
    contact: {
      ctaTitle: "Ready to source premium Indonesian vanilla?",
      chatWhatsapp: "Chat on WhatsApp",
      emailUs: "Email Us",
      visitUs: "Visit Us",
      getDirections: "Get Directions",
    },
    footer: {
      tagline: "Premium Indonesian vanilla, cured by hand and delivered to gourmet brands worldwide.",
      quickLinks: "Quick Links",
      products: "Products",
      productList: ["Vanilla Beans — Grade A", "Vanilla Extract", "Vanilla Powder", "Vanilla Seed (Caviar)", "Vanilla Paste"],
      contact: "Contact",
      rights: "All rights reserved.",
      madeIn: "Made in Indonesia",
    },
  },

  id: {
    code: "ID",
    nav: {
      home: "Beranda",
      about: "Tentang",
      products: "Produk",
      quality: "Kualitas",
      process: "Proses",
      gallery: "Galeri",
      contact: "Kontak",
    },
    common: {
      getQuote: "Minta Penawaran",
      enquire: "Tanya",
      downloadSpec: "Unduh Lembar Spesifikasi",
      copy: "Salin nomor",
      copied: "Tersalin!",
    },
    hero: {
      overline: "Dari Jantung Indonesia",
      title: ["Vanilla", "Premium", "Asli Indonesia"],
      titleAria: "Vanilla Premium Asli Indonesia",
      subtitle:
        "Menghadirkan vanilla Indonesia terbaik untuk produsen makanan, rumah perisa, dan merek gourmet di seluruh dunia.",
      watchStory: "Lihat Kisah Kami",
      scroll: "Gulir",
    },
    about: {
      overline: "Kisah Kami",
      title: "Berakar pada tanah vulkanik Jawa",
      p1: "Tropical Vanilla Co. lahir dari keyakinan sederhana — bahwa Indonesia menumbuhkan vanilla paling ekspresif di dunia, dan layak menjangkau dunia dengan karakternya yang utuh.",
      p2: "Kami bekerja bersama petani kecil di dataran tinggi Yogyakarta, mengolah setiap polong dengan cara tradisional dan menyortirnya secara manual. Hasilnya adalah vanilla yang pekat, gelap, dan lembut yang dipercaya oleh toko roti, rumah perisa, dan merek gourmet lintas benua.",
      p3: "Dari satu sulur hingga karton ekspor tersegel, kami menangani setiap langkah — sehingga kualitas yang Anda rasakan adalah kualitas yang kami janjikan.",
      stat1: "100%",
      stat1Label: "Asal Terlacak",
      stat2: "Grade A",
      stat2Label: "Standar Gourmet",
    },
    features: {
      overline: "Mengapa Memilih",
      title: "Tropical Vanilla",
      items: [
        { title: "Kualitas Premium", text: "Biji vanilla pilihan dengan kandungan vanilin tinggi dan kelembapan optimal." },
        { title: "Sumber Berkelanjutan", text: "Bekerja langsung dengan petani lokal menerapkan praktik berkelanjutan dan etis." },
        { title: "Pasokan Andal", text: "Kualitas dan pasokan konsisten sepanjang tahun untuk mendukung bisnis Anda." },
        { title: "Keahlian Ekspor", text: "Dokumentasi ekspor lengkap dan layanan profesional yang dapat Anda percaya." },
      ],
    },
    products: {
      overline: "Produk Kami",
      title: "Vanilla, dalam setiap bentuk",
      items: [
        {
          name: "Biji Vanilla — Grade A",
          tag: "Gourmet",
          text: "Polong gourmet panjang, mengkilap, dan kaya minyak yang disortir manual untuk aroma dan rasa istimewa.",
          specs: [["Kelembapan", "30 – 35%"], ["Vanilin", "> 2%"], ["Panjang", "16 – 20 cm"]],
        },
        {
          name: "Ekstrak Vanilla",
          tag: "Cair",
          text: "Vanilla alami yang diekstraksi perlahan dari biji Indonesia premium untuk rasa murni dan bulat.",
          specs: [["Tipe", "Single-fold"], ["Basis", "Alami"], ["Kegunaan", "Roti & susu"]],
        },
        {
          name: "Bubuk Vanilla",
          tag: "Giling",
          text: "Biji utuh yang digiling halus — sempurna untuk toko roti, es krim, cokelat, dan minuman.",
          specs: [["Gilingan", "Halus"], ["Kemurnian", "100% biji"], ["Masa simpan", "24 bulan"]],
        },
        {
          name: "Kaviar Vanilla (Biji)",
          tag: "Kaviar",
          text: "Biji vanilla hitam mungil yang berkilau, dikeluarkan dari polong yang dibelah — inti aroma vanilla yang paling murni.",
          specs: [["Bentuk", "Biji lepas"], ["Kemurnian", "100% biji"], ["Kegunaan", "Kue, es krim, konfeksi"]],
        },
        {
          name: "Pasta Vanilla",
          tag: "Pasta",
          text: "Pasta kaya aroma yang dibuat dari vanilla Indonesia premium untuk penggunaan praktis dalam pembuatan kue dan konfeksi.",
          specs: [
                ["Bentuk", "Pasta"],
                ["Basis", "Campuran bubuk dengan gliserin"],
                ["Kegunaan", "Dessert, bakery, es krim"],],},    
      ],
    },
    process: {
      overline: "Proses Kami",
      title: "Dari sulur hingga karton ekspor",
      subtitle: "Lima langkah cermat, semuanya dikerjakan manual, menjaga cita rasa yang menjadi ciri kami.",
      steps: [
        { title: "Panen", text: "Setiap polong dipetik dengan tangan pada kematangan sempurna, memastikan konsentrasi rasa terdalam sebelum pengolahan dimulai." },
        { title: "Pengolahan & Fermentasi", text: "Biji dijemur dan diperam dalam siklus tradisional selama berminggu-minggu, mengembangkan aroma pekat dan gelapnya secara alami." },
        { title: "Penyortiran & Penilaian", text: "Setiap biji diukur, diperiksa, dan dinilai manual agar setiap ikatan yang kami kirim memenuhi satu standar yang konsisten." },
        { title: "Pengemasan", text: "Dikemas vakum dalam kemasan food-grade di fasilitas higienis untuk menjaga kelembapan, aroma, dan kesegaran." },
        { title: "Ekspor", text: "Pengiriman tepat waktu ke seluruh dunia dengan dokumentasi ekspor lengkap, ditangani penuh oleh tim kami." },
      ],
    },
    quality: {
      overline: "Jaminan Kualitas",
      title: "Memenuhi standar internasional",
      text: "Setiap batch diuji untuk kelembapan, kandungan vanilin, dan keamanan pangan sebelum meninggalkan fasilitas kami — sehingga Anda menerima vanilla yang konsisten dan andal.",
      specs: [
        "Kelembapan: 30 – 35%",
        "Kandungan vanilin: > 2% (Grade A)",
        "Diuji mikroba & pestisida",
        "Sesuai standar keamanan pangan internasional",
        "Ketertelusuran penuh dari kebun hingga pelabuhan",
      ],
      cta: "Minta Spesifikasi",
    },
    gallery: { overline: "Galeri", title: "Dari perkebunan kami" },
    marquee: ["Ekspor Premium", "Pertanian Berkelanjutan", "Vanilla Gourmet", "Warisan Indonesia"],
    contact: {
      ctaTitle: "Siap memesan vanilla premium Indonesia?",
      chatWhatsapp: "Chat via WhatsApp",
      emailUs: "Email Kami",
      visitUs: "Kunjungi Kami",
      getDirections: "Petunjuk Arah",
    },
    footer: {
      tagline: "Vanilla premium Indonesia, diolah dengan tangan dan dikirim ke merek gourmet di seluruh dunia.",
      quickLinks: "Tautan Cepat",
      products: "Produk",
      productList: ["Biji Vanilla — Grade A", "Ekstrak Vanilla", "Bubuk Vanilla", "Biji Vanilla (Kaviar)", "Pasta Vanilla"],
      contact: "Kontak",
      rights: "Hak cipta dilindungi.",
      madeIn: "Dibuat di Indonesia",
    },
  },
};
